import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Box, CircularProgress, Tooltip,
} from '@mui/material';
import { useToasts } from 'react-toast-notifications';
import { auth } from '../../firebase';
import CreateKeyDialog from './CreateKeyDialog';
import UsageDetail from './UsageDetail';
import IpAccessDialog from './IpAccessDialog';

// Make firebase auth available globally for child components
window.firebaseAuth = auth;

const API = process.env.REACT_APP_API_BASE_URL;
const BID = process.env.REACT_APP_DECORMANAGER_BUILDER_ID;

const ApiKeys = () => {
  const { addToast } = useToasts();

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [usageKey, setUsageKey] = useState(null);
  const [revokeKey, setRevokeKey] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [editLimit, setEditLimit] = useState('');
  const [ipKey, setIpKey] = useState(null);
  const [builders, setBuilders] = useState([]);

  const fetchKeys = useCallback(async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const resp = await fetch(
        `${API}/auth/external-api-keys/list?builder_id=${BID}&authorization=${token}`,
      );
      const result = await resp.json();
      if (result.success) {
        setKeys(result.data.keys);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch keys', e);
      addToast('Failed to load API keys', { appearance: 'error' });
    }
    setLoading(false);
  }, [addToast]);

  const fetchBuilders = useCallback(async () => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/decormanager/public/salescenters`,
      );
      const result = await resp.json();
      if (result.data) {
        setBuilders(result.data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch builders', e);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
    fetchBuilders();
  }, [fetchKeys, fetchBuilders]);

  const handleRevoke = async () => {
    if (!revokeKey) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const resp = await fetch(
        `${API}/auth/external-api-keys/revoke?builder_id=${BID}&authorization=${token}&key_id=${revokeKey.id}`,
        { method: 'PATCH' },
      );
      const result = await resp.json();
      if (result.success) {
        addToast('API key revoked', { appearance: 'success' });
        fetchKeys();
      } else {
        addToast(result.error?.message || 'Failed to revoke', { appearance: 'error' });
      }
    } catch (e) {
      addToast(e.message, { appearance: 'error' });
    }
    setRevokeKey(null);
  };

  const handleUpdateLimit = async () => {
    if (!editKey) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const newLimit = editLimit === '' ? null : parseInt(editLimit, 10);
      const resp = await fetch(
        `${API}/auth/external-api-keys/update?builder_id=${BID}&authorization=${token}&key_id=${editKey.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthlyLimit: newLimit }),
        },
      );
      const result = await resp.json();
      if (result.success) {
        addToast('Limit updated', { appearance: 'success' });
        fetchKeys();
      } else {
        addToast(result.error?.message || 'Failed to update', { appearance: 'error' });
      }
    } catch (e) {
      addToast(e.message, { appearance: 'error' });
    }
    setEditKey(null);
  };

  return (
    <Container fixed maxWidth="lg">
      <Box className="pageContainer" sx={{ py: 3 }}>
        <Box sx={
{
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
}
}
        >
          <Typography variant="h5">External API Keys</Typography>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create New Key
          </Button>
        </Box>

        {
loading ? (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
) : (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Label</TableCell>
          <TableCell>Key Prefix</TableCell>
          <TableCell>Builder</TableCell>
          <TableCell>Permissions</TableCell>
          <TableCell>Created</TableCell>
          <TableCell>Last Used</TableCell>
          <TableCell>Usage / Limit</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {
keys.map((key) => (
  <TableRow key={key.id} sx={{ opacity: key.isActive ? 1 : 0.5 }}>
    <TableCell>{key.label}</TableCell>
    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
      {key.keyPrefix}
      ...
    </TableCell>
    <TableCell>{key.builderId}</TableCell>
    <TableCell>
      {
key.permissions?.map((p) => (
  <Chip key={p} label={p} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
))
}
    </TableCell>
    <TableCell>{key.createdAt?.split('T')[0]}</TableCell>
    <TableCell>{key.lastUsedAt ? key.lastUsedAt.split('T')[0] : 'Never'}</TableCell>
    <TableCell>
      <Tooltip title="Click for details">
        <Button size="small" onClick={() => setUsageKey(key)} sx={{ textTransform: 'none' }}>
          {key.currentMonthUsage.toLocaleString()}
          {key.monthlyLimit ? ` / ${key.monthlyLimit.toLocaleString()}` : ''}
        </Button>
      </Tooltip>
    </TableCell>
    <TableCell>
      <Chip
        label={key.isActive ? 'Active' : 'Revoked'}
        color={key.isActive ? 'success' : 'default'}
        size="small"
      />
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {
key.isActive && (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    <Button
      size="small"
      variant="outlined"
      onClick={() => setIpKey(key)}
      sx={{ minWidth: 0, px: 1, fontSize: 12 }}
    >
      IPs
      {
(key.allowedIps?.length > 0 || key.blockedIps?.length > 0) && (
  <Chip
    label={`${key.allowedIps?.length || 0}/${key.blockedIps?.length || 0}`}
    size="small"
    sx={{ ml: 0.5, height: 18, fontSize: 10 }}
  />
)
}
    </Button>
    <Button
      size="small"
      variant="outlined"
      onClick={
        () => {
          setEditKey(key);
          setEditLimit(key.monthlyLimit?.toString() || '');
        }
      }
      sx={{ minWidth: 0, px: 1, fontSize: 12 }}
    >
      Limit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      onClick={() => setRevokeKey(key)}
      sx={{ minWidth: 0, px: 1, fontSize: 12 }}
    >
      Revoke
    </Button>
  </Box>
)
}
    </TableCell>
  </TableRow>
))
}
        {
keys.length === 0 && (
  <TableRow>
    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
      <Typography color="text.secondary">No API keys created yet.</Typography>
    </TableCell>
  </TableRow>
)
}
      </TableBody>
    </Table>
  </TableContainer>
)
}
      </Box>

      {/* Create Key Dialog */}
      <CreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchKeys}
        builders={builders}
      />

      {/* IP Access Control Dialog */}
      <IpAccessDialog
        open={!!ipKey}
        onClose={() => setIpKey(null)}
        apiKey={ipKey}
        onUpdated={fetchKeys}
      />

      {/* Usage Detail Dialog */}
      <UsageDetail
        open={!!usageKey}
        onClose={() => setUsageKey(null)}
        apiKey={usageKey}
      />

      {/* Revoke Confirmation Dialog */}
      <Dialog open={!!revokeKey} onClose={() => setRevokeKey(null)}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke
            {' '}
            <strong>{revokeKey?.label}</strong>
            {' '}
            (
            {revokeKey?.keyPrefix}
            ...)?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeKey(null)}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained">Revoke</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Limit Dialog */}
      <Dialog open={!!editKey} onClose={() => setEditKey(null)}>
        <DialogTitle>
          Update Monthly Limit:
          {' '}
          {editKey?.label}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Monthly limit (leave empty for unlimited)"
            type="number"
            value={editLimit}
            onChange={(e) => setEditLimit(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditKey(null)}>Cancel</Button>
          <Button onClick={handleUpdateLimit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeys;
